import React, { useState } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  Send, 
  Shield, 
  CheckCircle,
  Calendar,
  MessageSquare,
  Users
} from 'lucide-react';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    service: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Save to database
      const { error: dbError } = await supabase
        .from('contacts')
        .insert([{
          name: formData.name,
          email: formData.email,
          phone: formData.phone || null,
          company: formData.company || null,
          service_interest: formData.service || null,
          message: formData.message
        }]);

      if (dbError) {
        console.error('Database error:', dbError);
        throw new Error('Failed to save contact information');
      }

      // Send via Web3Forms to info@zentech.co.ke
      const web3formData = new FormData();
      web3formData.append('access_key', '8419ce0c-65f7-4a0e-95d1-936c89f2eb90');
      web3formData.append('email', 'info@zentech.co.ke');
      web3formData.append('subject', `New Contact Form Submission from ${formData.name}`);
      web3formData.append('from_name', formData.name);
      web3formData.append('from_email', formData.email);
      web3formData.append('phone', formData.phone || 'Not provided');
      web3formData.append('company', formData.company || 'Not provided');
      web3formData.append('service', formData.service || 'Not specified');
      web3formData.append('message', formData.message);

      const web3Response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: web3formData
      });

      if (!web3Response.ok) {
        console.error('Web3Forms error:', await web3Response.text());
      }

      // Send email notification
      const { error: emailError } = await supabase.functions.invoke('send-contact-email', {
        body: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          service: formData.service,
          message: formData.message
        }
      });

      if (emailError) {
        console.error('Email error:', emailError);
        // Don't throw error for email failure - contact was still saved
      }

      toast({
        title: "Message Sent Successfully!",
        description: "Our IT experts will contact you within 24 hours to discuss your requirements.",
      });
      
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        service: '',
        message: ''
      });
    } catch (error) {
      console.error('Contact form error:', error);
      toast({
        title: "Error",
        description: "There was a problem sending your message. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone Support',
      details: '0106193053',
      description: '24/7 Emergency Support Available'
    },
    {
      icon: Mail,
      title: 'Email Us',
      details: 'info@zentech.co.ke',
      description: 'Response within 2 hours'
    },
    {
      icon: MapPin,
      title: 'Our Location',
      details: 'Nairobi, Kenya',
      description: 'P.O.BOX 48764-00100'
    },
    {
      icon: Clock,
      title: 'Business Hours',
      details: 'Mon-Fri: 8:00 AM - 6:00 PM',
      description: 'Emergency support: 24/7'
    }
  ];

  const services = [
    'Managed IT Services',
    'Cybersecurity Solutions',
    'Cloud Services',
    'Network Management',
    'Data Backup & Recovery',
    'Compliance Management',
    'IT Consultation',
    'Other'
  ];

  const benefits = [
    'Free initial consultation and IT assessment',
    'Custom solutions tailored to your business',
    'Transparent pricing with no hidden costs',
    'Dedicated account manager assigned',
    '24/7 monitoring and support included'
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary to-primary-light text-primary-foreground py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl lg:text-6xl font-bold mb-6">
              Get Your Free IT Consultation
            </h1>
            <p className="text-xl lg:text-2xl text-primary-foreground/90 leading-relaxed">
              Ready to transform your IT infrastructure? Contact our experts today for a 
              free assessment and customized solution for your business.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form and Info */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <Card className="border-none shadow-xl">
                <CardHeader>
                  <CardTitle className="text-2xl text-foreground flex items-center space-x-2">
                    <MessageSquare className="h-6 w-6 text-primary" />
                    <span>Send Us a Message</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Full Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          placeholder="Your full name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email Address *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          placeholder="your.email@company.com"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="company">Company Name</Label>
                        <Input
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleInputChange}
                          placeholder="Your company name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone Number</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="+44 20 1234 5678"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="service">Service Interest</Label>
                      <select
                        id="service"
                        name="service"
                        value={formData.service}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <option value="">Select a service</option>
                        {services.map((service, index) => (
                          <option key={index} value={service}>{service}</option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        rows={5}
                        placeholder="Tell us about your IT challenges and requirements..."
                      />
                    </div>

                    <Button 
                      type="submit" 
                      size="lg" 
                      className="w-full" 
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        'Sending...'
                      ) : (
                        <>
                          Send Message <Send className="h-4 w-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-foreground mb-6">Get In Touch</h2>
                <p className="text-lg text-muted-foreground mb-8">
                  Our IT experts are ready to help you solve your technology challenges and 
                  enhance your cybersecurity posture. Reach out to us through any of the 
                  following channels.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {contactInfo.map((info, index) => (
                  <Card key={index} className="border-none shadow-md hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start space-x-4">
                        <div className="bg-primary/10 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0">
                          <info.icon className="h-6 w-6 text-primary" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-foreground mb-1">{info.title}</h3>
                          <p className="text-primary font-medium mb-1">{info.details}</p>
                          <p className="text-sm text-muted-foreground">{info.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Benefits */}
              <Card className="bg-gradient-to-br from-primary/5 to-primary-light/5 border-primary/20">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-foreground mb-4 flex items-center space-x-2">
                    <Shield className="h-5 w-5 text-primary" />
                    <span>What You Get</span>
                  </h3>
                  <div className="space-y-3">
                    {benefits.map((benefit, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <CheckCircle className="h-5 w-5 text-success flex-shrink-0 mt-0.5" />
                        <span className="text-foreground text-sm">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Contact Options */}
      <section className="py-20 bg-muted">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
              Need Immediate Assistance?
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Choose the contact method that works best for you. Our team is ready to help 
              with your IT and cybersecurity needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="text-center border-none shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <Phone className="h-16 w-16 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-bold text-foreground mb-3">Call Now</h3>
                <p className="text-muted-foreground mb-4">
                  Speak directly with our IT experts for immediate assistance.
                </p>
                <Button asChild className="w-full">
                  <a href="tel:0106193053">0106193053</a>
                </Button>
              </CardContent>
            </Card>

            <Card className="text-center border-none shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <Calendar className="h-16 w-16 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-bold text-foreground mb-3">Schedule Meeting</h3>
                <p className="text-muted-foreground mb-4">
                  Book a convenient time for a detailed consultation.
                </p>
                <Button variant="outline" className="w-full">
                  Book Consultation
                </Button>
              </CardContent>
            </Card>

            <Card className="text-center border-none shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <Users className="h-16 w-16 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-bold text-foreground mb-3">Live Chat</h3>
                <p className="text-muted-foreground mb-4">
                  Get instant answers to your IT questions online.
                </p>
                <Button variant="outline" className="w-full">
                  Start Chat
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Emergency Contact */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <Card className="bg-gradient-to-r from-destructive to-destructive/80 text-destructive-foreground border-none">
            <CardContent className="p-12 text-center">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                IT Emergency? We're Here 24/7
              </h2>
              <p className="text-xl text-destructive-foreground/90 mb-8 max-w-3xl mx-auto">
                Experiencing a critical IT issue or security breach? Our emergency response 
                team is available around the clock to help resolve urgent situations.
              </p>
              <Button asChild variant="secondary" size="lg">
                <a href="tel:0106193053">Emergency Hotline: 0106193053</a>
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>
    </Layout>
  );
};

export default Contact;